import { useAccountStore } from "@/common/store/accountStore";
import {
  canViewFeature,
  canWriteFeature,
  type ViewFeature,
  type WriteFeature,
} from "@/common/accessControl";
import type { AffiliationType } from "@/api/affiliationType";
import type { AccountRole } from "@/pages/login/type/loginType";

/**
 * 로그인 계정의 소속/권한 기반 UI 분기 프리미티브.
 *
 * 보안 경계가 아니라 UI 구성 전용이다. 실제 차단은 서버 가드(403)가 담당한다.
 * 화면별 메뉴/버튼 노출·활성 여부는 모두 이 훅을 통해 결정한다.
 */
export function usePermission() {
  const user = useAccountStore((s) => s.user_info);
  const affiliationType: AffiliationType | null =
    user?.affiliation?.type ?? null;
  const role: AccountRole | null = user?.role ?? null;

  return {
    affiliationType,
    role,
    isMaster: affiliationType === "MASTER",
    isCpo: affiliationType === "CPO",
    isClient: affiliationType === "CLIENT",
    isAdmin: role === "ADMIN",
    /**
     * 조회 항목 노출 게이트. 정책은 accessControl 의 VIEW_ACCESS_BY_FEATURE 단일 출처를 따른다.
     * 소속 분류를 직접 비교하는 대신 이 함수를 쓰면 "누가 무엇을 보는가" 가 표 한 곳에 모인다.
     */
    canView: (feature: ViewFeature) => canViewFeature(affiliationType, feature),
    // 등록/수정/삭제 쓰기 액션 게이트 (관리자 전용이 기본).
    // 엑셀 다운로드는 조회 권한만으로 허용하므로 여기에 걸지 않는다
    canWrite: canWriteFeature(role),
    /**
     * 일반 직책도 쓰기가 허용되는 기능의 게이트.
     * 정책은 accessControl 의 MEMBER_WRITABLE_FEATURES 단일 출처를 따른다.
     */
    canWriteFeature: (feature: WriteFeature) => canWriteFeature(role, feature),
  };
}
